import { Injectable } from '@angular/core';
import { Competition } from '@models';
import { combineLatest, map, Observable, shareReplay, switchMap } from 'rxjs';
import { FirestoreService } from './firestore.service';
import { SportContextService } from './sport-context.service';
import { TenantContextService } from './tenant-context.service';
import { where } from '@angular/fire/firestore';

@Injectable({
  providedIn: 'root',
})
export class CompetitionService {
  public competitions$: Observable<Competition[]>;

  constructor(
    private firestore: FirestoreService,
    private tenantContext: TenantContextService,
    private sportContext: SportContextService,
  ) {
    this.competitions$ = combineLatest([
      this.tenantContext.tenantId$,
      this.sportContext.activeSport$,
      this.sportContext.isMultiSport$,
    ]).pipe(
      switchMap(([, activeSport, isMultiSport]) => {
        const constraints =
          isMultiSport && activeSport
            ? [where('sport', '==', activeSport)]
            : [];
        return this.firestore.listDocuments<Competition>(
          this.tenantContext.collections.COMPETITIONS,
          ...constraints,
        );
      }),
      shareReplay(1),
    );
  }

  private get col() {
    return this.tenantContext.collections.COMPETITIONS;
  }

  get(id: string) {
    return this.competitions$.pipe(
      map((competition) => competition.find((c) => c.id === id)!),
    );
  }

  create(competition: Omit<Competition, 'id'>) {
    const sport = this.sportContext.getActiveSport();
    const data =
      sport && !(competition as any).sport
        ? { ...competition, sport }
        : competition;
    return this.firestore.create<Competition>(this.col, data);
  }

  update(id: string, competition: Partial<Competition>) {
    return this.firestore.update<Competition>(this.col, { id, ...competition });
  }

  createOrUpdate(id: string | undefined, competition: Omit<Competition, 'id'>) {
    return id ? this.update(id, competition) : this.create(competition);
  }

  getReport() {
    return this.firestore.listDocuments<Competition>(this.col);
  }
}
